import React, { useEffect, useMemo, useState } from "react";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import Alert from "../../../Common/Alert";

function formatDate(date) {
  if (!date) return "";
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getToday() {
  return formatDate(new Date());
}

export default function FinancialSummary() {
  IsLogin();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [date, setDate] = useState(getToday());
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);
        setError("");
        await fetchData();
      } catch (e) {
        setError(e?.message || "Failed to load data");
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  const fetchData = async (params) => {
    const d = params?.date || date;
    const url = `/accounts/financial-summary?date=${d}`;
    const res = await Http.get(url);
    const payload = res?.data?.data || res?.data || null;
    setSummary(payload);
  };

  // Summary fields from API response
  const depositToday = Number(summary?.depositToday ?? 0);
  const depositOpening = Number(summary?.depositOpening ?? 0);

  const dailyCollectionToday = Number(summary?.dailyCollectionToday ?? 0);
  const dailyCollectionOpening = Number(summary?.dailyCollectionOpening ?? 0);

  const pettyCashTransferToday = Number(summary?.pettyCashTransferToday ?? 0);
  const pettyCashTransferOpening = Number(
    summary?.pettyCashTransferOpening ?? 0
  );

  const balance = Number(summary?.balance ?? 0);
  const totalBalance = Number(summary?.totalBalance ?? 0);
  const totalExpenses = Number(summary?.totalExpenses ?? 0);

  const expensesToday = Number(summary?.expensesToday ?? 0);
  const expensesOpening = Number(summary?.expensesOpening ?? 0);

  const vendorPaymentsToday = Number(summary?.vendorPaymentsToday ?? 0);
  const vendorPaymentsOpening = Number(summary?.vendorPaymentsOpening ?? 0);

  // Use API calculated totals
  const openingBalance = Number(summary?.openingBalance ?? 0);
  const totalCollectionToday = Number(summary?.totalCollectionToday ?? 0);
  const totalCollectionOpening = Number(summary?.totalCollectionOpening ?? 0);

  const totalExpensesToday = Number(summary?.totalExpensesToday ?? 0);
  const totalExpensesOpening = Number(summary?.totalExpensesOpening ?? 0);

  const balanceToday = Number(summary?.balanceToday ?? 0);
  const balanceOpening = Number(summary?.balanceOpening ?? 0);
  const balanceFinal =
    Number(summary?.balanceToday ?? 0) + Number(summary?.openingBalance ?? 0);

  // Get transaction arrays
  const inflowTransactions = summary?.in || [];
  const outflowTransactions = summary?.out || [];

  const onApplyFilter = async () => {
    try {
      setLoading(true);
      setError("");
      await fetchData({ date });
    } catch (e) {
      setError(e?.message || "Failed to filter data");
    } finally {
      setLoading(false);
    }
  };

  const onToday = async () => {
    const t = getToday();
    setDate(t);
    try {
      setLoading(true);
      setError("");
      await fetchData({ date: t });
    } catch (e) {
      setError(e?.message || "Failed to filter data");
    } finally {
      setLoading(false);
    }
  };

  const currency = (v) => {
    // Remove commas if the value is a string
    const cleanValue = typeof v === "string" ? v.replace(/,/g, "") : v;
    const n = Number(cleanValue || 0);
    return n.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const cleanDescription = (description) => {
    if (!description) return "";

    // Remove "Deposit to bank: [Bank Name] -" pattern for inflows
    let cleaned = description.replace(/Deposit to bank:\s*[^-]+-\s*/i, "");

    // Remove "- Bank: [Bank Name]" pattern for outflows
    cleaned = cleaned.replace(/-\s*Bank:\s*[^$]+$/i, "");

    return cleaned.trim();
  };

  return (
    <div>
      <IsLogin></IsLogin>
      <div>
        <div className="am-mainpanel">
          <div className="am-pagetitle">
            {" "}
            <h5 className="am-title">Financial Summary</h5>
          </div>
          <div className="am-pagebody">
            <div className="container-fluid p-3">
              <div className="card pd-20 pd-sm-40">
                {/* Header Section */}
                <div className="text-center mb-4">
                  <h3
                    className="mb-1"
                    style={{ color: "#2c3e50", fontWeight: "bold" }}
                  >
                    DAILY CASH STATEMENT
                  </h3>
                  <p className="mb-0 text-muted">
                    Restaurant Financial Summary
                  </p>
                  <hr
                    style={{
                      width: "200px",
                      margin: "10px auto",
                      borderTop: "2px solid #3498db",
                    }}
                  />
                </div>

                {/* Date Filter Section */}
                <div className="row mb-4">
                  <div className="col-md-4 offset-md-4">
                    <div
                      className="card"
                      style={{
                        backgroundColor: "#f8f9fa",
                        border: "1px solid #dee2e6",
                      }}
                    >
                      <div className="card-body p-3">
                        <div className="row align-items-end">
                          <div className="col-8">
                            <label className="form-label fw-bold">
                              Statement Date:
                            </label>
                            <input
                              type="date"
                              className="form-control"
                              value={date}
                              onChange={(e) => setDate(e.target.value)}
                              style={{ border: "2px solid #3498db" }}
                            />
                          </div>
                          <div className="col-4">
                            <button
                              className="btn btn-primary w-100 mb-1"
                              onClick={onApplyFilter}
                              disabled={loading}
                            >
                              Apply
                            </button>
                            <button
                              className="btn btn-outline-secondary w-100"
                              onClick={onToday}
                              disabled={loading}
                            >
                              Today
                            </button>
                          </div>
                        </div>
                        {loading && (
                          <div className="text-center mt-2">
                            <span className="text-primary">Loading…</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {error ? (
                  <div className="row mb-3">
                    <div className="col-12">
                      <Alert type="danger" message={error} />
                    </div>
                  </div>
                ) : null}

                {/* Opening Balance Section */}
                {/* <div className="row mb-4">
                  <div className="col-12">
                    <div className="card" style={{backgroundColor: '#e8f4fd', border: '2px solid #3498db'}}>
                      <div className="card-header" style={{backgroundColor: '#3498db', color: 'white'}}>
                        <h5 className="mb-0"><i className="fas fa-balance-scale me-2"></i>Balance</h5>
                      </div>
                      <div className="card-body">
                        <div className="row text-center">
                          <div className="col-md-3">
                            <h6 className="text-muted">Total Opening</h6>
                            <h4 className="text-primary">{currency(totalCollectionOpening)}</h4>
                          </div>
                           <div className="col-md-3">
                            <h6 className="text-muted">Total Collection</h6>
                            <h4 className="text-primary">{currency(totalBalance)}</h4>
                          </div>
                          <div className="col-md-3">
                            <h6 className="text-muted">Total Expenses</h6>
                            <h4 className="text-danger">{currency(totalExpenses)}</h4>
                          </div>
                          <div className="col-md-3">
                            <h6 className="text-muted">Net Balance</h6>
                            <h4 className={balance >= 0 ? "text-success" : "text-danger"}>
                              {currency(balance)}
                            </h4>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div> */}

                {/* Cash Receipts Section */}
                <div className="row mb-4">
                  <div className="col-md-6">
                    <div
                      className="card h-100"
                      style={{ border: "2px solid #27ae60" }}
                    >
                      <div
                        className="card-header"
                        style={{ backgroundColor: "#27ae60", color: "white" }}
                      >
                        <h5 className="mb-0">
                          <i className="fas fa-arrow-down me-2"></i>CASH
                          RECEIPTS (Inflows)
                        </h5>
                      </div>
                      <div className="card-body pb-0">
                        <table className="table table-sm mb-0">
                          <tbody>
                            <tr>
                              <td>Bank Deposits</td>
                              <td className="text-end fw-bold">
                                {currency(depositToday)}
                              </td>
                            </tr>
                            <tr>
                              <td>Fund Transfers to Petty Cash</td>
                              <td className="text-end fw-bold">
                                {currency(pettyCashTransferToday)}
                              </td>
                            </tr>
                            <tr
                              style={{
                                backgroundColor: "#d4edda",
                                borderTop: "2px solid #27ae60",
                              }}
                            >
                              <td className="fw-bold">Total Receipts Today</td>
                              <td className="text-end fw-bold text-success">
                                {currency(totalCollectionToday)}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  {/* Cash Payments Section */}
                  <div className="col-md-6">
                    <div
                      className="card h-100"
                      style={{ border: "2px solid #e74c3c" }}
                    >
                      <div
                        className="card-header"
                        style={{ backgroundColor: "#e74c3c", color: "white" }}
                      >
                        <h5 className="mb-0">
                          <i className="fas fa-arrow-up me-2"></i>CASH PAYMENTS
                          (Outflows)
                        </h5>
                      </div>
                      <div className="card-body pb-0">
                        <table className="table table-sm mb-0">
                          <tbody>
                            <tr>
                              <td>Operating Expenses</td>
                              <td className="text-end fw-bold">
                                {currency(expensesToday)}
                              </td>
                            </tr>
                            <tr>
                              <td>Vendor Payments</td>
                              <td className="text-end fw-bold">
                                {currency(vendorPaymentsToday)}
                              </td>
                            </tr>
                            <tr
                              style={{
                                backgroundColor: "#f8d7da",
                                borderTop: "2px solid #e74c3c",
                              }}
                            >
                              <td className="fw-bold">Total Payments Today</td>
                              <td className="text-end fw-bold text-danger">
                                {currency(totalExpensesToday)}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Net Cash Flow Section */}
                <div className="row mb-4">
                  <div className="col-12">
                    <div
                      className="card"
                      style={{
                        backgroundColor: "#fff3cd",
                        border: "2px solid #ffc107",
                      }}
                    >
                      <div
                        className="card-header"
                        style={{ backgroundColor: "#ffc107", color: "#212529" }}
                      >
                        <h5 className="mb-0">
                          <i className="fas fa-calculator me-2"></i>Net Cash
                          Flow Summary
                        </h5>
                      </div>
                      <div className="card-body pb-0">
                        <div className="row">
                          <div className="col-md-8">
                            <table className="table table-borderless mb-0">
                              <tbody>
                                <tr>
                                  <td className="fw-bold">Opening Balance:</td>
                                  <td className="text-end text-success fw-bold">
                                    {currency(openingBalance)}
                                  </td>
                                </tr>
                                <tr>
                                  <td className="fw-bold">
                                    Total Cash Receipts:
                                  </td>
                                  <td className="text-end text-success fw-bold">
                                    {currency(totalCollectionToday)}
                                  </td>
                                </tr>
                                <tr>
                                  <td className="fw-bold">
                                    Less: Total Cash Payments:
                                  </td>
                                  <td className="text-end text-danger fw-bold">
                                    ({currency(totalExpensesToday)})
                                  </td>
                                </tr>
                                <tr style={{ borderTop: "2px solid #ffc107" }}>
                                  <td className="fw-bold fs-5">
                                    Net Cash Flow Today:
                                  </td>
                                  <td
                                    className={`text-end fw-bold fs-5 ${
                                      balanceFinal >= 0
                                        ? "text-success"
                                        : "text-danger"
                                    }`}
                                  >
                                    {currency(balanceFinal)}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                          <div className="col-md-4 text-center">
                            <div className="mb-2">
                              <h6 className="mb-1">
                                {`Today's Transaction Balance`}
                              </h6>
                              <h3
                                className={
                                  balanceToday >= 0
                                    ? "text-success"
                                    : "text-danger"
                                }
                              >
                                {currency(balanceToday)}
                              </h3>
                            </div>
                            <div
                              className={`p-3 rounded ${
                                balanceFinal >= 0 ? "bg-success" : "bg-danger"
                              }`}
                              style={{ color: "white" }}
                            >
                              <h6 className="mb-1">Cash Position</h6>
                              <h3 className="mb-0">
                                {balanceFinal >= 0 ? "POSITIVE" : "NEGATIVE"}
                              </h3>
                              <small>
                                {balanceFinal >= 0 ? "Surplus" : "Deficit"}
                              </small>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* NEW CARD: Transaction Details */}
                <div className="row mb-4">
                  <div className="col-md-6">
                    <div
                      className="card h-100"
                      style={{ border: "2px solid #27ae60" }}
                    >
                      <div
                        className="card-header"
                        style={{ backgroundColor: "#27ae60", color: "white" }}
                      >
                        <h5 className="mb-0">
                          <i className="fas fa-list me-2"></i>Inflow Transaction
                          Details
                        </h5>
                      </div>
                      <div
                        className="card-body"
                        style={{ maxHeight: "400px", overflowY: "auto" }}
                      >
                        {inflowTransactions.length > 0 ? (
                          <table className="table table-sm table-striped">
                            <thead
                              style={{
                                position: "sticky",
                                top: 0,
                                backgroundColor: "#fff",
                                zIndex: 1,
                              }}
                            >
                              <tr>
                                <th style={{ width: "70%" }}>Description</th>
                                <th
                                  className="text-end"
                                  style={{ width: "30%" }}
                                >
                                  Amount
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {inflowTransactions.map((transaction) => (
                                <tr key={transaction.id}>
                                  <td style={{ fontSize: "0.9rem" }}>
                                    {cleanDescription(transaction.description)}
                                  </td>
                                  <td
                                    className="text-end fw-bold text-success"
                                    style={{ fontSize: "0.9rem" }}
                                  >
                                    {currency(transaction.amount)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        ) : (
                          <div className="text-center text-muted py-4">
                            <p>No inflow transactions for this date</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div
                      className="card h-100"
                      style={{ border: "2px solid #e74c3c" }}
                    >
                      <div
                        className="card-header"
                        style={{ backgroundColor: "#e74c3c", color: "white" }}
                      >
                        <h5 className="mb-0">
                          <i className="fas fa-list me-2"></i>Outflow
                          Transaction Details
                        </h5>
                      </div>
                      <div
                        className="card-body"
                        style={{ maxHeight: "400px", overflowY: "auto" }}
                      >
                        {outflowTransactions.length > 0 ? (
                          <table className="table table-sm table-striped">
                            <thead
                              style={{
                                position: "sticky",
                                top: 0,
                                backgroundColor: "#fff",
                                zIndex: 1,
                              }}
                            >
                              <tr>
                                <th style={{ width: "70%" }}>Description</th>
                                <th
                                  className="text-end"
                                  style={{ width: "30%" }}
                                >
                                  Amount
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {outflowTransactions.map((transaction) => (
                                <tr key={transaction.id}>
                                  <td style={{ fontSize: "0.9rem" }}>
                                    {cleanDescription(transaction.description)}
                                  </td>
                                  <td
                                    className="text-end fw-bold text-danger"
                                    style={{ fontSize: "0.9rem" }}
                                  >
                                    {currency(transaction.amount)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        ) : (
                          <div className="text-center text-muted py-4">
                            <p>No outflow transactions for this date</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="text-center mt-4">
                  <small className="text-muted">
                    Generated on {new Date().toLocaleString()} | Restaurant ERP
                    System
                  </small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
